import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Filter, X, Bookmark, BookmarkCheck, LayoutGrid, Scale, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { toast } from 'sonner';
import { useAnalytics } from '../hooks/useAnalytics';
import { getMaterialImageUrl, PLACEHOLDER_IMAGE } from '../utils/materialImages';

const MaterialLibrary = () => {
  const { t } = useTranslation();
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [materials, setMaterials] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const { trackMaterialView, trackMaterialSaved, trackMaterialCompared } = useAnalytics();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('size')) || 24);
  const [totalMaterials, setTotalMaterials] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const gridRef = useRef(null);
  
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    stone_type: searchParams.get('type') || '',
    color: searchParams.get('color') || '',
    finish: searchParams.get('finish') || '',
    application: searchParams.get('application') || ''
  });

  const stoneTypes = ['Marble', 'Quartzite', 'Limestone', 'Travertine', 'Onyx', 'Granite', 'Sandstone'];
  const colors = ['White', 'Grey', 'Beige', 'Green', 'Blue', 'Brown', 'Black', 'Red'];
  const finishes = ['Polished', 'Honed', 'Brushed', 'Leathered', 'Sandblasted'];
  const applications = ['Countertop', 'Wall', 'Floor', 'Bathroom', 'Fireplace', 'Outdoor'];

  const pageSizeOptions = [12, 24, 48, 96];

  // Scroll to top of grid when page changes
  const scrollToGrid = useCallback(() => {
    if (gridRef.current) {
      gridRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    const fetchMaterials = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('page', currentPage.toString());
        params.append('page_size', pageSize.toString());
        if (filters.search) params.append('search', filters.search);
        if (filters.stone_type) params.append('stone_type', filters.stone_type);
        if (filters.color) params.append('color', filters.color);
        if (filters.finish) params.append('finish', filters.finish);
        if (filters.application) params.append('application', filters.application);

        const response = await axios.get(`${API}/materials?${params.toString()}`);
        const data = response.data;
        
        // Handle new paginated response format
        if (data.materials) {
          setMaterials(data.materials);
          setTotalMaterials(data.total || 0);
          setTotalPages(data.total_pages || 1);
        } else {
          // Fallback for old format (array of materials)
          setMaterials(data);
          setTotalMaterials(data.length);
          setTotalPages(1);
        }
      } catch (error) {
        console.error('Failed to fetch materials:', error);
        toast.error('Errore nel caricamento dei materiali');
      } finally {
        setLoading(false);
      }
    };

    const fetchSaved = async () => {
      try {
        const response = await axios.get(`${API}/saved-materials`, { withCredentials: true });
        setSavedIds(new Set(response.data.map(m => m.material_id)));
      } catch {}
    };

    fetchMaterials();
    fetchSaved();
  }, [filters, currentPage, pageSize]);

  // Update URL when pagination changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set('page', currentPage.toString());
    params.set('size', pageSize.toString());
    setSearchParams(params, { replace: true });
  }, [currentPage, pageSize]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      scrollToGrid();
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(parseInt(newSize));
    setCurrentPage(1); // Reset to first page when changing page size
    scrollToGrid();
  };

  const handleFilterChange = (key, value) => {
    // Convert "all" sentinel value back to empty string for actual filtering
    const actualValue = value === 'all' ? '' : value;
    const newFilters = { ...filters, [key]: actualValue };
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
    
    // Update URL params
    const params = new URLSearchParams();
    if (newFilters.search) params.set('search', newFilters.search);
    if (newFilters.stone_type) params.set('type', newFilters.stone_type);
    if (newFilters.color) params.set('color', newFilters.color);
    if (newFilters.finish) params.set('finish', newFilters.finish);
    if (newFilters.application) params.set('application', newFilters.application);
    params.set('page', '1');
    params.set('size', pageSize.toString());
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({ search: '', stone_type: '', color: '', finish: '', application: '' });
    setCurrentPage(1);
    setSearchParams({ page: '1', size: pageSize.toString() });
  };

  const toggleSave = async (materialId, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (savedIds.has(materialId)) {
        await axios.delete(`${API}/saved-materials/${materialId}`, { withCredentials: true });
        setSavedIds(prev => {
          const next = new Set(prev);
          next.delete(materialId);
          return next;
        });
        toast.success(t('materials.materialRemoved'));
      } else {
        await axios.post(`${API}/saved-materials/${materialId}`, {}, { withCredentials: true });
        setSavedIds(prev => new Set([...prev, materialId]));
        trackMaterialSaved(materialId);
        toast.success(t('materials.materialSaved'));
      }
    } catch (error) {
      toast.error(t('materials.failedToSave'));
    }
  };

  const hasActiveFilters = filters.stone_type || filters.color || filters.finish || filters.application;

  const toggleCompareSelect = (materialId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (selectedForCompare.includes(materialId)) {
      setSelectedForCompare(selectedForCompare.filter(id => id !== materialId));
    } else {
      if (selectedForCompare.length >= 4) {
        toast.error('Puoi confrontare massimo 4 materiali');
        return;
      }
      setSelectedForCompare([...selectedForCompare, materialId]);
    }
  };

  const goToCompare = () => {
    if (selectedForCompare.length < 2) {
      toast.error('Seleziona almeno 2 materiali da confrontare');
      return;
    }
    trackMaterialCompared(selectedForCompare);
    navigate(`/compare/materials?ids=${selectedForCompare.join(',')}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Content with padding for fixed header */}
      <div className="pt-20">
      <main className="max-w-[1800px] mx-auto px-6 md:px-12 py-12">
        {/* Onboarding Source Banner */}
        {searchParams.get('source') === 'onboarding' && filters.color && (
          <div className="mb-6 px-4 py-3 bg-stone-100 border border-stone-200 flex items-center justify-between" data-testid="onboarding-source-banner">
            <p className="text-sm text-stone-700">
              Results based on your <strong className="text-stone-900">{filters.color}</strong> color selection
            </p>
            <button
              onClick={() => {
                setSearchParams({});
                setFilters({ search: '', stone_type: '', color: '', finish: '', application: '' });
              }}
              className="text-sm text-stone-500 hover:text-stone-900 underline"
            >
              Clear filter
            </button>
          </div>
        )}
        
        {/* Breadcrumb */}
        <Breadcrumb currentPage={t('nav.materials')} />
        
        {/* Page Header */}
        <div className="mb-12">
          <p className="caption-text text-muted-foreground mb-2">{t('materials.library')}</p>
          <h1 className="heading-display text-4xl md:text-5xl font-medium" data-testid="library-title">
            {t('materials.title')}
          </h1>
        </div>

        {/* Search & Filters */}
        <div className="mb-12">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="input-icon-wrapper flex-1 max-w-md">
              <Search className="input-icon" />
              <Input
                type="text"
                placeholder={t('materials.searchPlaceholder')}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="input-field with-icon-left w-full"
                data-testid="search-input"
              />
            </div>
            <Button 
              variant="outline" 
              className="btn-secondary inline-flex items-center gap-2"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4" />
              {t('common.filters')}
              {hasActiveFilters && <span className="w-2 h-2 bg-primary rounded-full"></span>}
            </Button>
            <Button 
              variant={compareMode ? "default" : "outline"}
              className="inline-flex items-center gap-2"
              onClick={() => {
                setCompareMode(!compareMode);
                if (compareMode) setSelectedForCompare([]);
              }}
              data-testid="compare-mode-btn"
            >
              <Scale className="w-4 h-4" />
              {compareMode ? 'Esci' : 'Confronta'}
            </Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="bg-secondary/30 p-6 mb-6 animate-fade-in-up" data-testid="filter-panel">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="caption-text text-muted-foreground mb-2 block">{t('materials.stoneType')}</label>
                  <Select value={filters.stone_type || 'all'} onValueChange={(v) => handleFilterChange('stone_type', v)}>
                    <SelectTrigger className="input-field" data-testid="filter-type">
                      <SelectValue placeholder={t('materials.allTypes')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('materials.allTypes')}</SelectItem>
                      {stoneTypes.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="caption-text text-muted-foreground mb-2 block">{t('materials.color')}</label>
                  <Select value={filters.color || 'all'} onValueChange={(v) => handleFilterChange('color', v)}>
                    <SelectTrigger className="input-field" data-testid="filter-color">
                      <SelectValue placeholder={t('materials.allColors')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('materials.allColors')}</SelectItem>
                      {colors.map(color => (
                        <SelectItem key={color} value={color}>{color}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="caption-text text-muted-foreground mb-2 block">{t('materials.finish')}</label>
                  <Select value={filters.finish || 'all'} onValueChange={(v) => handleFilterChange('finish', v)}>
                    <SelectTrigger className="input-field" data-testid="filter-finish">
                      <SelectValue placeholder={t('materials.allFinishes')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('materials.allFinishes')}</SelectItem>
                      {finishes.map(finish => (
                        <SelectItem key={finish} value={finish}>{finish}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="caption-text text-muted-foreground mb-2 block">{t('materials.application')}</label>
                  <Select value={filters.application || 'all'} onValueChange={(v) => handleFilterChange('application', v)}>
                    <SelectTrigger className="input-field" data-testid="filter-application">
                      <SelectValue placeholder={t('materials.allApplications')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('materials.allApplications')}</SelectItem>
                      {applications.map(app => (
                        <SelectItem key={app} value={app}>{app}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  className="mt-4 text-sm"
                  onClick={clearFilters}
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4 mr-2" /> {t('materials.clearAllFilters')}
                </Button>
              )}
            </div>
          )}

          {/* Results count and Page Size selector */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              {totalMaterials === 0 
                ? 'Nessun materiale trovato'
                : totalMaterials === 1 
                  ? '1 materiale trovato'
                  : `${totalMaterials} materiali trovati`}
              {totalPages > 1 && ` • Pagina ${currentPage} di ${totalPages}`}
            </p>
            
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Mostra:</span>
              <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-[80px] input-field" data-testid="page-size-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pageSizeOptions.map(size => (
                    <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">per pagina</span>
            </div>
          </div>
        </div>

        {/* Materials Grid */}
        <div ref={gridRef}>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] bg-secondary mb-4"></div>
                <div className="h-3 bg-secondary w-20 mb-2"></div>
                <div className="h-5 bg-secondary w-32"></div>
              </div>
            ))}
          </div>
        ) : materials.length === 0 ? (
          <div className="text-center py-24">
            <LayoutGrid className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="heading-display text-xl font-medium mb-2">{t('materials.noMaterialsFound')}</h3>
            <p className="text-muted-foreground mb-6">{t('materials.tryAdjustingFilters')}</p>
            <Button onClick={clearFilters} className="btn-secondary" data-testid="no-results-clear-btn">
              {t('materials.clearFilters')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16">
            {materials.map((material) => (
              <Link 
                key={material.material_id} 
                to={compareMode ? '#' : `/materials/${material.material_id}`}
                onClick={compareMode ? (e) => toggleCompareSelect(material.material_id, e) : undefined}
                className={`material-card group relative ${compareMode && selectedForCompare.includes(material.material_id) ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                data-testid={`material-card-${material.material_id}`}
              >
                <div className="aspect-[3/4] overflow-hidden mb-4 bg-secondary relative">
                  <img 
                    src={getMaterialImageUrl(material)} 
                    alt={material.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMAGE; }}
                  />
                  {compareMode ? (
                    <div 
                      className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        selectedForCompare.includes(material.material_id) 
                          ? 'bg-primary text-white' 
                          : 'bg-white/90 border-2 border-gray-300'
                      }`}
                    >
                      {selectedForCompare.includes(material.material_id) && <Check className="w-5 h-5" />}
                    </div>
                  ) : (
                    <button
                      onClick={(e) => toggleSave(material.material_id, e)}
                      className="absolute top-4 right-4 p-2 bg-white/90 hover:bg-white transition-colors"
                      data-testid={`save-btn-${material.material_id}`}
                    >
                      {savedIds.has(material.material_id) ? (
                        <BookmarkCheck className="w-5 h-5 text-primary" />
                      ) : (
                        <Bookmark className="w-5 h-5" />
                      )}
                    </button>
                  )}
                </div>
                <p className="caption-text text-muted-foreground mb-1">{material.stone_type}</p>
                <h3 className="heading-display text-lg font-medium">{material.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">{material.color}</p>
              </Link>
            ))}
          </div>
        )}
        
        {/* Pagination Controls */}
        {!loading && totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-16 pb-8">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="hidden sm:flex"
                data-testid="pagination-first"
              >
                Prima
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                data-testid="pagination-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {(() => {
                  const pages = [];
                  const maxVisible = 5;
                  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                  let end = Math.min(totalPages, start + maxVisible - 1);
                  
                  if (end - start + 1 < maxVisible) {
                    start = Math.max(1, end - maxVisible + 1);
                  }
                  
                  if (start > 1) {
                    pages.push(
                      <Button key={1} variant="ghost" size="sm" onClick={() => handlePageChange(1)}>1</Button>
                    );
                    if (start > 2) pages.push(<span key="dots1" className="px-1 text-muted-foreground">...</span>);
                  }
                  
                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <Button
                        key={i}
                        variant={currentPage === i ? "default" : "ghost"}
                        size="sm"
                        onClick={() => handlePageChange(i)}
                        data-testid={`pagination-page-${i}`}
                      >
                        {i}
                      </Button>
                    );
                  }
                  
                  if (end < totalPages) {
                    if (end < totalPages - 1) pages.push(<span key="dots2" className="px-1 text-muted-foreground">...</span>);
                    pages.push(
                      <Button key={totalPages} variant="ghost" size="sm" onClick={() => handlePageChange(totalPages)}>{totalPages}</Button>
                    );
                  }
                  
                  return pages;
                })()}
              </div>
              
              <Button
                variant="outline"
                size="icon"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                data-testid="pagination-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="hidden sm:flex"
                data-testid="pagination-last"
              >
                Ultima
              </Button>
            </div>
          </div>
        )}
        </div>
      </main>
      </div>
      
      <Footer />
      
      {/* Compare Bar (fixed at bottom when in compare mode) */}
      {compareMode && selectedForCompare.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-50">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">
                {selectedForCompare.length} materiali selezionati
              </span>
              <button 
                onClick={() => setSelectedForCompare([])}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Deseleziona tutti
              </button>
            </div>
            <Button 
              onClick={goToCompare}
              disabled={selectedForCompare.length < 2}
              data-testid="go-to-compare-btn"
            >
              <Scale className="w-4 h-4 mr-2" />
              Confronta ({selectedForCompare.length})
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialLibrary;
